/**
 * MergeDialog - Generic dialog component for merging entities (authors, readers, tags)
 *
 * Displays a confirmation dialog with source and target entity selection
 */
package com.castly.admin.ui.components

import androidx.compose.runtime.*
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

data class MergeEntity(
    val id: Int,
    val name: String,
    val count: Int? = null // Optional count of associated items (e.g., audiobooks)
)

@Composable
fun MergeDialog(
    entityType: String, // e.g., "Author", "Reader", "Tag"
    sourceEntity: MergeEntity,
    targetEntities: List<MergeEntity>,
    isLoading: Boolean = false,
    errorMessage: String? = null,
    onConfirm: (targetId: Int) -> Unit,
    onCancel: () -> Unit
) {
    val colors = AppTheme.colors
    var selectedTargetId by remember { mutableStateOf<Int?>(null) }

    // Modal overlay
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "1000")
        }
        onClick { if (!isLoading) onCancel() }
    }) {
        // Dialog content
        Div({
            style {
                backgroundColor(Color(colors.surface))
                borderRadius(BorderRadius.lg)
                padding(Spacing.xxl)
                maxWidth(600.px)
                width(90.percent)
                property("box-shadow", "0 20px 25px -5px rgba(0, 0, 0, 0.1)")
                themeTransition()
            }
            onClick { it.stopPropagation() } // Prevent closing when clicking inside
        }) {
            // Header
            H2({
                style {
                    margin(0.px)
                    marginBottom(Spacing.lg)
                    fontSize(Typography.xl)
                    fontWeight("600")
                    color(Color(colors.text))
                }
            }) {
                Text("Merge $entityType")
            }

            // Warning message
            Div({
                style {
                    backgroundColor(Color(if (colors == LightColors) "#fef3c7" else "#451a03"))
                    border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#fbbf24" else "#854d0e"))
                    borderRadius(BorderRadius.md)
                    padding(Spacing.md)
                    marginBottom(Spacing.lg)
                }
            }) {
                Div({
                    style {
                        fontSize(Typography.base)
                        fontWeight("600")
                        color(Color(if (colors == LightColors) "#92400e" else "#fcd34d"))
                        marginBottom(Spacing.xs)
                    }
                }) {
                    Text("Warning")
                }
                Div({
                    style {
                        fontSize(Typography.base)
                        color(Color(if (colors == LightColors) "#78350f" else "#fcd34d"))
                    }
                }) {
                    Text("This action cannot be undone. All associations from the source ${entityType.lowercase()} will be transferred to the target.")
                }
            }

            // Error message
            errorMessage?.let { error ->
                Div({
                    style {
                        backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                        border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#fca5a5" else "#7f1d1d"))
                        borderRadius(BorderRadius.md)
                        padding(Spacing.md)
                        marginBottom(Spacing.lg)
                    }
                }) {
                    Div({
                        style {
                            fontSize(Typography.base)
                            fontWeight("600")
                            color(Color(if (colors == LightColors) "#991b1b" else "#fca5a5"))
                            marginBottom(Spacing.xs)
                        }
                    }) {
                        Text("Error")
                    }
                    Div({
                        style {
                            fontSize(Typography.base)
                            color(Color(colors.danger))
                        }
                    }) {
                        Text(error)
                    }
                }
            }

            // Source entity (read-only)
            Div({
                style {
                    marginBottom(Spacing.lg)
                }
            }) {
                Div({
                    style {
                        fontSize(Typography.base)
                        fontWeight("500")
                        color(Color(colors.text))
                        marginBottom(BorderRadius.md)
                    }
                }) {
                    Text("Source $entityType (will be deleted)")
                }

                Div({
                    style {
                        padding(Spacing.md)
                        backgroundColor(Color(colors.surfaceHover))
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        fontSize(Typography.base)
                        color(Color(colors.text))
                    }
                }) {
                    Text(sourceEntity.name)
                    sourceEntity.count?.let { count ->
                        Span({
                            style {
                                marginLeft(Spacing.sm)
                                fontSize(13.px)
                                color(Color(colors.textMuted))
                            }
                        }) {
                            Text("($count associated)")
                        }
                    }
                }
            }

            // Target entity selector
            Div({
                style {
                    marginBottom(Spacing.xl)
                }
            }) {
                Div({
                    style {
                        fontSize(Typography.base)
                        fontWeight("500")
                        color(Color(colors.text))
                        marginBottom(BorderRadius.md)
                    }
                }) {
                    Text("Target $entityType (keep this one)")
                    Span({
                        style {
                            color(Color(colors.danger))
                            marginLeft(2.px)
                        }
                    }) {
                        Text("*")
                    }
                }

                Select({
                    onInput { selectedTargetId = it.value?.toIntOrNull() }
                    if (isLoading) disabled()
                    style {
                        width(100.percent)
                        padding(10.px, Spacing.md)
                        fontSize(Typography.base)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        property("outline", "none")
                        property("box-sizing", "border-box")
                        if (isLoading) {
                            backgroundColor(Color(colors.surfaceHover))
                            cursor("not-allowed")
                        }
                    }
                }) {
                    Option("", {
                        disabled()
                        if (selectedTargetId == null) selected()
                    }) {
                        Text("Select target ${entityType.lowercase()}")
                    }
                    targetEntities.forEach { entity ->
                        Option(entity.id.toString(), {
                            if (selectedTargetId == entity.id) selected()
                        }) {
                            Text(buildString {
                                append(entity.name)
                                entity.count?.let { append(" ($it associated)") }
                            })
                        }
                    }
                }
            }

            // Result preview
            if (selectedTargetId != null) {
                Div({
                    style {
                        backgroundColor(Color(if (colors == LightColors) "#ecfdf5" else "#052e16"))
                        border(1.px, LineStyle.Solid, Color(colors.success))
                        borderRadius(BorderRadius.md)
                        padding(Spacing.md)
                        marginBottom(Spacing.lg)
                    }
                }) {
                    Div({
                        style {
                            fontSize(Typography.base)
                            fontWeight("500")
                            color(Color(if (colors == LightColors) "#065f46" else "#86efac"))
                            marginBottom(Spacing.xs)
                        }
                    }) {
                        Text("Result")
                    }
                    Div({
                        style {
                            fontSize(Typography.base)
                            color(Color(if (colors == LightColors) "#047857" else "#86efac"))
                        }
                    }) {
                        val targetName = targetEntities.find { it.id == selectedTargetId }?.name ?: ""
                        val sourceCount = sourceEntity.count ?: 0
                        val targetCount = targetEntities.find { it.id == selectedTargetId }?.count ?: 0
                        Text("\"$targetName\" will have ${sourceCount + targetCount} associated items after merge.")
                    }
                }
            }

            // Action buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(Spacing.md)
                    justifyContent(JustifyContent.FlexEnd)
                }
            }) {
                Button({
                    onClick { if (!isLoading) onCancel() }
                    if (isLoading) disabled()
                    style {
                        padding(10.px, Spacing.xl)
                        fontSize(Typography.base)
                        fontWeight("500")
                        color(Color(colors.text))
                        backgroundColor(Color(colors.surface))
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        cursor(if (isLoading) "not-allowed" else "pointer")
                        property("transition", "all 0.2s")
                    }
                }) {
                    Text("Cancel")
                }

                Button({
                    onClick {
                        selectedTargetId?.let { targetId ->
                            if (!isLoading) onConfirm(targetId)
                        }
                    }
                    if (selectedTargetId == null || isLoading) disabled()
                    style {
                        padding(10.px, Spacing.xl)
                        fontSize(Typography.base)
                        fontWeight("500")
                        color(Color("#ffffff"))
                        backgroundColor(if (selectedTargetId == null || isLoading) Color(colors.textMuted) else Color(colors.danger))
                        border(0.px)
                        borderRadius(BorderRadius.md)
                        cursor(if (selectedTargetId == null || isLoading) "not-allowed" else "pointer")
                        property("transition", "all 0.2s")
                    }
                }) {
                    Text(if (isLoading) "Merging..." else "Confirm Merge")
                }
            }
        }
    }
}
